import { useAppUi } from '../../app-ui/AppUiScope';
import baseline, { appTextStyle, appCopy } from '../../app-ui/baseline';
import ContentTitle from '../../components/content/ContentTitle';
import RecoverableImage from '../images/RecoverableImage';
import PlaylistArtwork from '../../components/content/PlaylistArtwork';
import React, { useEffect } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { reviewsApi } from '../../api/reviews';
import { useNearby } from '../discovery/useNearby';
import { Link } from 'react-router-dom';
import ContentShelf from '../../components/responsive/ContentShelf';
import { DiscoverySection } from '../../components/content/DiscoveryContent';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useRecentContent, removeUnavailableRecent } from './recentContentStore';
import {
  needsRecentPlaylistArtwork,
  recentPlaylistArtwork,
  recentAlbumRequest,
  readRecentRating,
  readRecentPlaylist,
} from './recentArtwork';
import './RecentContent.css';

export default function RecentContent({ identity = [] }) {
  const appUi = useAppUi();
  const { items, scope } = useRecentContent();
  const [nearRef, nearby] = useNearby();
  const reviews = items.filter((item) => item.kind === 'review');
  const checks = useQueries({
    queries: reviews.map((item) => ({
      queryKey: ['web-recent-review-check', scope, item.id, identity],
      queryFn: ({ signal }) =>
        reviewsApi.getReviewById(item.id.slice('review:'.length), {
          signal,
          suppressGlobalError: true,
        }),
      enabled: nearby,
      staleTime: 30000,
      retry: false,
    })),
  });
  useEffect(() => {
    checks.forEach((query, index) =>
      removeUnavailableRecent(scope, reviews[index].path, query.error),
    );
  }, [checks, reviews, scope]);
  const { t, locale } = useWebLocale();
  if (!items.length) return null;
  const kinds = { album: t('albums'), review: t('reviews'), playlist: t('playlists') };
  return (
    <div ref={nearRef}>
      <DiscoverySection title={t('home.continueBrowsing')}>
        <ContentShelf
          className={appUi ? 'pp-recent-app-shelf' : ''}
          label={t('home.continueBrowsing')}
        >
          {items.map((item) => (
            <RecentCard
              key={item.id}
              item={item}
              scope={scope}
              identity={identity}
              appUi={appUi}
              locale={locale}
              kinds={kinds}
            />
          ))}
        </ContentShelf>
      </DiscoverySection>
    </div>
  );
}

function RecentCard({ item, scope, identity, appUi, locale, kinds }) {
  const [nearRef, nearby] = useNearby();
  const artwork = useQuery({
    queryKey: ['web-recent-playlist-artwork', scope, identity, item.path],
    queryFn: ({ signal }) => readRecentPlaylist(item, signal),
    enabled: nearby && needsRecentPlaylistArtwork(item),
    staleTime: 300000,
    retry: false,
  });
  const rating = useQuery({
    queryKey: ['web-recent-explicit-rating', scope, identity, item.path],
    queryFn: ({ signal }) => readRecentRating(item, signal),
    enabled: nearby && item.contentRating === undefined && Boolean(recentAlbumRequest(item)),
    staleTime: 3600000,
    retry: false,
  });
  useEffect(() => {
    removeUnavailableRecent(scope, item.path, artwork.error);
  }, [scope, item.path, artwork.error]);
  return (
    <Link ref={nearRef} to={item.path} className="pp-recent-card">
      <div className="pp-recent-art">
        {item.kind === 'playlist' ? (
          <PlaylistArtwork playlist={recentPlaylistArtwork(item, artwork.data)} />
        ) : item.imageUrl ? (
          <RecoverableImage src={item.imageUrl} alt="" loading="lazy" />
        ) : null}
      </div>
      {!appUi && <small className="pp-secondary">{kinds[item.kind]}</small>}
      {appUi ? (
        <ContentTitle
          title={item.title}
          explicit={item.kind !== 'playlist' && (item.contentRating ?? rating.data) === 'EXPLICIT'}
        />
      ) : (
        <strong>{item.title}</strong>
      )}
      {item.subtitle && (
        <span
          className={appUi && item.kind === 'album' ? 'pp-recent-artist' : 'pp-secondary'}
          style={
            appUi
              ? appTextStyle(
                  item.kind === 'album'
                    ? baseline.contentText.artist
                    : baseline.contentText.metadata,
                )
              : undefined
          }
        >
          {appUi && item.kind !== 'album'
            ? appCopy(
                locale,
                item.kind === 'review' ? 'home.recentReviewBy' : 'home.recentPlaylistBy',
                { name: item.subtitle },
              )
            : item.kind === 'review'
              ? locale === 'en'
                ? `Review by ${item.subtitle}`
                : `${item.subtitle}님의 리뷰`
              : item.subtitle}
        </span>
      )}
    </Link>
  );
}
